import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

export default function ServicoForm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const veiculoIdParam = params.get('veiculoId');
  const clienteIdParam = params.get('clienteId');
  const { isAdmin, isProprietario, usuario } = useAuth();

  const [clientes, setClientes] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [mecanicos, setMecanicos] = useState([]);
  const [buscaCliente, setBuscaCliente] = useState('');
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [form, setForm] = useState({
    cliente: clienteIdParam || '',
    veiculo: veiculoIdParam || '',
    kmEntrada: '',
    responsavel: (isAdmin && !isProprietario) ? '' : (usuario?.nome || ''),
    diagnostico: '',
    status: 'orcamento',
    desconto: 0,
    pecas: [],
    maosDeObra: [],
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/clientes').then(r => setClientes(r.data));
    if (isAdmin && !isProprietario) api.get('/auth/usuarios').then(r => setMecanicos(r.data.filter(u => u.perfil === 'mecanico' && u.ativo !== false)));
    if (clienteIdParam) carregarVeiculos(clienteIdParam);
  }, []);

  useEffect(() => {
    if (clienteIdParam && clientes.length > 0) {
      const c = clientes.find(x => x._id === clienteIdParam);
      if (c) setClienteSelecionado(c);
    }
  }, [clientes, clienteIdParam]);

  const carregarVeiculos = async clienteId => {
    const { data } = await api.get(`/clientes/${clienteId}/veiculos`);
    setVeiculos(data);
  };

  const selecionarCliente = async c => {
    setForm(f => ({ ...f, cliente: c._id, veiculo: '' }));
    setClienteSelecionado(c);
    setBuscaCliente(c.nome);
    await carregarVeiculos(c._id);
  };

  const salvar = async e => {
    e.preventDefault();
    if (!form.cliente || !form.veiculo) return toast.error('Selecione cliente e veículo');
    setLoading(true);
    try {
      const payload = { ...form, veiculoId: form.veiculo, clienteId: form.cliente };
      const { data } = await api.post('/servicos', payload);
      toast.success('OS criada! Faça o checkin do veículo.');
      navigate(`/checkin/${data._id}`);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao salvar');
    } finally {
      setLoading(false);
    }
  };

  const clientesFiltrados = buscaCliente && !clienteSelecionado
    ? clientes.filter(c =>
        c.nome.toLowerCase().includes(buscaCliente.toLowerCase()) ||
        c.telefone.includes(buscaCliente)
      )
    : [];

  return (
    <div className="max-w-lg mx-auto">
      <button onClick={() => navigate(-1)} className="text-blue-600 text-sm mb-4 hover:underline">← Voltar</button>

      {/* Indicador de etapas */}
      <div className="flex items-center gap-2 mb-6">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">1</div>
          <span className="text-sm font-semibold text-blue-700">Dados Básicos</span>
        </div>
        <div className="flex-1 h-0.5 bg-gray-200 mx-1" />
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gray-200 text-gray-400 text-sm font-bold flex items-center justify-center">2</div>
          <span className="text-sm text-gray-400">Checking do Veículo</span>
        </div>
        <div className="flex-1 h-0.5 bg-gray-200 mx-1" />
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gray-200 text-gray-400 text-sm font-bold flex items-center justify-center">3</div>
          <span className="text-sm text-gray-400">Peças e Serviços</span>
        </div>
      </div>

      <form onSubmit={salvar} className="space-y-4">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <h2 className="font-semibold text-gray-700 mb-3">Cliente e Veículo</h2>

          {!clienteIdParam && (
            <div className="mb-3 relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Buscar Cliente</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Nome ou telefone..."
                value={buscaCliente}
                onChange={e => {
                  setBuscaCliente(e.target.value);
                  setClienteSelecionado(null);
                  setVeiculos([]);
                  setForm(f => ({ ...f, cliente: '', veiculo: '' }));
                }}
              />
              {clientesFiltrados.length > 0 && (
                <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1 max-h-48 overflow-y-auto">
                  {clientesFiltrados.map(c => (
                    <div key={c._id} className="p-3 hover:bg-gray-50 cursor-pointer" onClick={() => selecionarCliente(c)}>
                      <div className="font-medium">{c.nome}</div>
                      <div className="text-sm text-gray-500">{c.telefone}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {clienteSelecionado && (
            <div className="bg-blue-50 rounded-lg p-3 mb-3 text-sm">
              <span className="font-medium">{clienteSelecionado.nome}</span> — {clienteSelecionado.telefone}
            </div>
          )}

          {veiculos.length > 0 && (
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Veículo *</label>
              <select
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.veiculo}
                onChange={e => setForm(f => ({ ...f, veiculo: e.target.value }))}
              >
                <option value="">Selecione...</option>
                {veiculos.map(v => (
                  <option key={v._id} value={v._id}>
                    {v.placa} — {v.marca} {v.modelo} {v.ano}
                  </option>
                ))}
              </select>
            </div>
          )}

          {form.veiculo && (
            <button
              type="button"
              onClick={() => navigate(`/historico/${form.veiculo}`)}
              className="w-full mb-3 border border-blue-200 text-blue-600 py-2 rounded-lg text-sm font-medium hover:bg-blue-50 flex items-center justify-center gap-1.5"
            >
              📋 Ver Histórico deste Veículo (peças, garantias e orçamentos anteriores)
            </button>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">KM Entrada</label>
              <input
                type="number"
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={form.kmEntrada}
                onChange={e => setForm(f => ({ ...f, kmEntrada: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mecânico Responsável *</label>
              {(isAdmin && !isProprietario) ? (
                <select
                  required
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={form.responsavel}
                  onChange={e => setForm(f => ({ ...f, responsavel: e.target.value }))}
                >
                  <option value="">Selecione...</option>
                  {mecanicos.map(m => (
                    <option key={m.id} value={m.nome}>{m.nome}</option>
                  ))}
                </select>
              ) : (
                <input
                  readOnly
                  className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2.5 text-base text-gray-600"
                  value={form.responsavel}
                />
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Diagnóstico / Problema Relatado — CLIENTE
          </label>
          <textarea
            rows={3}
            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            value={form.diagnostico}
            onChange={e => setForm(f => ({ ...f, diagnostico: e.target.value }))}
            placeholder="O que o cliente relatou..."
          />
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800 flex items-start gap-3">
          <span className="text-2xl">🔍</span>
          <div>
            <p className="font-semibold mb-0.5">Próximo passo: Checking do veículo</p>
            <p className="text-blue-700">Após salvar, você será direcionado para registrar as condições do veículo. Peças e serviços serão adicionados depois.</p>
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-semibold text-base hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? 'Salvando...' : <>Salvar e ir para o Checking →</>}
        </button>
      </form>
    </div>
  );
}
